import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { amiGet, amiPost, Card, Loading, NotYet, num, pct, tsFmt, errMsg } from "@/lib/ami";

const sel = "h-9 rounded-md border border-border bg-background px-2 text-sm";

function Compare({ strategies }) {
  const [sid, setSid] = useState(strategies[0]?.id || "");
  const [bar, setBar] = useState("4h");
  const m = useMutation({ mutationFn: () => amiPost("/backtests/compare-stops", { strategy_id: sid, bar }), onError: (e) => toast.error(errMsg(e)) });
  const opt = useMutation({ mutationFn: () => amiPost(`/strategies/${sid}/optimize`, { strategy_id: sid, bar }), onError: (e) => toast.error(errMsg(e)) });
  const r = m.data;
  const o = opt.data;
  return (
    <>
      <Card title="Stop-Loss-Modelle A–F vergleichen" testid="ami-stops-compare">
        <div className="flex flex-wrap gap-2 items-center">
          <select value={sid} onChange={(e) => setSid(e.target.value)} className={sel} data-testid="ami-stops-strategy">{strategies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
          <select value={bar} onChange={(e) => setBar(e.target.value)} className={sel} data-testid="ami-stops-bar"><option>1h</option><option>4h</option><option>1d</option></select>
          <Button onClick={() => m.mutate()} disabled={!sid || m.isPending} data-testid="ami-stops-run">{m.isPending ? "Rechnet…" : "Vergleichen"}</Button>
          <Button variant="secondary" onClick={() => opt.mutate()} disabled={!sid || opt.isPending} data-testid="ami-optimize-run">{opt.isPending ? "Optimiert…" : "Parameter optimieren"}</Button>
        </div>
        {r && (
          <div className="mt-4 overflow-x-auto"><table className="w-full text-xs font-mono" data-testid="ami-stops-table">
            <thead><tr className="text-left uppercase tracking-wider text-muted-foreground font-sans"><th className="py-2">Modell</th><th>Rendite</th><th>Max DD</th><th>Sharpe</th><th>Trades</th><th>Treffer</th><th>OOS Rendite</th><th>OOS Sharpe</th></tr></thead>
            <tbody>{Object.entries(r.results).map(([k, v]) => (
              <tr key={k} className="border-t border-border/60" data-testid={`ami-stops-row-${k}`}><td className="py-1.5 font-sans">{k} · {r.models[k]}</td><td>{pct(v.metrics.total_return_pct, 1)}</td><td>{pct(v.metrics.max_drawdown_pct, 1)}</td><td>{num(v.metrics.sharpe, 2)}</td><td>{v.metrics.trades}</td><td>{num(v.metrics.win_rate_pct, 0)} %</td><td>{pct(v.out_of_sample.total_return_pct, 1)}</td><td>{num(v.out_of_sample.sharpe, 2)}</td></tr>
            ))}</tbody>
          </table><p className="mt-2 text-xs text-muted-foreground">{r.note} Stops werden erst nach Kerzenschluss nachgezogen und nie gelockert.</p></div>
        )}
      </Card>
      {o && (
        <Card title="Optimierung" testid="ami-optimize-result">
          <div className={`text-sm mb-2 ${o.overfit_warning ? "text-amber-300" : "text-emerald-400"}`} data-testid="ami-overfit">{o.overfit_warning ? "Warnung: Im Testzeitraum deutlich schlechter – wahrscheinlich Überanpassung." : "Testzeitraum bestätigt die Richtung."}</div>
          <div className="text-xs font-mono">Beste Parameter {JSON.stringify(o.best_params)} · Entwicklung Sharpe {num(o.best_in_sample.sharpe, 2)} / {pct(o.best_in_sample.total_return_pct, 1)} · Test ab {tsFmt(o.split_ts)} Sharpe {num(o.out_of_sample.sharpe, 2)} / {pct(o.out_of_sample.total_return_pct, 1)}</div>
          <p className="mt-2 text-xs text-muted-foreground">{o.note}</p>
        </Card>
      )}
    </>
  );
}

export default function AmiStopLoss() {
  const q = useQuery({ queryKey: ["ami-strategies"], queryFn: () => amiGet("/strategies") });
  if (q.isLoading) return <Loading />;
  const items = q.data.items.filter((s) => s.type !== "prediction" || true);
  return <div className="space-y-6" data-testid="ami-stoploss">{items.length ? <Compare strategies={items} /> : <NotYet text="Lege zuerst unter Strategieentwicklung eine Strategie an." />}</div>;
}
